import { useState, useEffect, useCallback } from 'react'
import BackOfficeAPIClient from "../../../clients/BackofficeAPIClient"
import { PlafondRemise } from '../../../types/PlafondRemise'

interface UsePlafondsRemiseReturn {
    plafondsRemise: PlafondRemise[]
    loading: boolean
    saving: boolean
    hasChanges: boolean
    updatePlafond: (IDSociete: number | null, IDProfil: number, value: string | number | null, IDLibelleRemise?: number | null) => void
    savePlafondsRemise: () => Promise<boolean>
}

const usePlafondsRemise = (): UsePlafondsRemiseReturn => {
    const backofficeApiClient = new BackOfficeAPIClient();
    const [plafondsRemise, setPlafondsRemise] = useState<PlafondRemise[]>([])
    const [updatedIds, setUpdatedIds] = useState<number[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [saving, setSaving] = useState<boolean>(false)

    const fetchPlafondsRemise = useCallback(async () => {
        try {
            setLoading(true)
            const data = await backofficeApiClient.get<PlafondRemise[]>("/plafondsRemise")
            setPlafondsRemise(data)
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchPlafondsRemise()
    }, [fetchPlafondsRemise])

    const updatePlafond = useCallback((IDSociete: number | null, IDProfil: number, value: string | number | null, IDLibelleRemise: number | null = null) => {
        setPlafondsRemise(prev => {
            const idx = prev.findIndex(p =>
                (p.IDPlaque ?? null) === (IDSociete ?? null) &&
                p.IDDRProfileEsellerLead === IDProfil &&
                (p.IDLibelleRemise ?? null) === (IDLibelleRemise ?? null)
            )

            if (idx !== -1) {
                const updated = [...prev]
                updated[idx] = { ...updated[idx], Plafond: value }

                const plafondId = updated[idx].IDPlafondRemise
                if (plafondId) {
                    setUpdatedIds(ids => ids.includes(plafondId) ? ids : [...ids, plafondId])
                }

                return updated
            } else {
                return [...prev, {
                    IDPlaque: IDSociete,
                    IDDRProfileEsellerLead: IDProfil,
                    IDLibelleRemise: IDLibelleRemise,
                    Plafond: value
                }]
            }
        })
    }, [])

    const savePlafondsRemise = useCallback(async (): Promise<boolean> => {
        setSaving(true)
        try {
            const updatedPlafondsRemise = plafondsRemise.filter(
                p => !p.IDPlafondRemise || updatedIds.includes(p.IDPlafondRemise)
            )

            await backofficeApiClient.patch("/plafondsRemise", {plafondsRemise: updatedPlafondsRemise})
            setUpdatedIds([])

            // Refetch pour récupérer les IDs des nouvelles entrées
            await fetchPlafondsRemise()

            return true
        } catch (error) {
            console.log(error)
            throw error
        } finally {
            setSaving(false)
        }
    }, [plafondsRemise, updatedIds, fetchPlafondsRemise])

    const hasChanges = updatedIds.length > 0 || plafondsRemise.some(p => !p.IDPlafondRemise)

    return {
        plafondsRemise,
        loading,
        saving,
        hasChanges,
        updatePlafond,
        savePlafondsRemise
    }
}

export default usePlafondsRemise
